import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useLanguageSwitch } from '@/hooks/useLanguage';
import SharedNavbar from '@/components/feature/SharedNavbar';
import SharedFooter from '@/components/feature/SharedFooter';
import ProtectedRoute from '@/components/feature/ProtectedRoute';

interface MyLayoutProps {
  children: React.ReactNode;
  title?: string;
}

export default function MyLayout({ children, title }: MyLayoutProps) {
  const { t } = useTranslation();
  const { currentUser, logout } = useAuth();
  const { currentLang } = useLanguageSwitch();
  const prefix = currentLang === 'ko' ? '/ko' : currentLang === 'bn' ? '/bn' : '';
  const location = useLocation();

  const tabs = [
    { href: '/my', label: t('my.dashboard', 'Dashboard'), icon: 'ri-dashboard-line' },
    { href: '/my/profile', label: t('my.profile.title', 'Profile'), icon: 'ri-user-line' },
    { href: '/my/orders', label: t('my.orders.title', 'Orders'), icon: 'ri-shopping-bag-line' },
    { href: '/my/qna', label: t('my.qna.title', 'Q&A'), icon: 'ri-question-line' },
    { href: '/stores', label: t('nav.whereToBuy', 'Where to Buy'), icon: 'ri-map-pin-line' },
  ];

  const isActive = (href: string) => {
    const fullPath = prefix + href;
    if (href === '/my') {
      return location.pathname === fullPath || location.pathname === href;
    }
    return location.pathname.startsWith(fullPath) || location.pathname.startsWith(href);
  };

  return (
    <ProtectedRoute>
      <div className="print:hidden">
        <SharedNavbar />
      </div>
      <div className="w-full min-h-screen bg-[#FAFAF9] pt-24 pb-20 px-4 md:px-8 print:pt-0 print:pb-0 print:px-0 print:min-h-0 print:bg-white">
        <div className="max-w-6xl mx-auto">
          {/* Profile Summary */}
          <div className="bg-white rounded-2xl p-6 md:p-8 border border-[#E8E8E6] mb-6 print:hidden">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              <div className="w-16 h-16 rounded-full bg-[#F0EFED] flex items-center justify-center text-xl font-serif text-[#7B1113]">
                {currentUser?.name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1">
                <h1 className="font-serif text-xl font-bold text-[#2A2A2A]">{currentUser?.name}</h1>
                <p className="text-sm text-[#8A8A8A]">{currentUser?.email}</p>
                <div className="flex flex-wrap gap-4 mt-3">
                  <span className="text-xs text-[#8A8A8A]">
                    {t('my.joinDate', 'Member since')}: {currentUser?.joinDate}
                  </span>
                  <span className="text-xs text-[#8A8A8A]">
                    {t('my.grade', 'Grade')}: {currentUser?.memberGrade}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {title && (
            <h2 className="font-serif text-lg font-bold text-[#2A2A2A] mb-4 print:hidden">{title}</h2>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 print:block">
            {/* Sidebar */}
            <div className="lg:col-span-1 print:hidden">
              <div className="bg-white rounded-2xl border border-[#E8E8E6] overflow-hidden">
                {tabs.map((tab) => (
                  <Link
                    key={tab.href}
                    to={prefix + tab.href}
                    className={`flex items-center gap-2 px-5 py-3.5 text-sm transition-colors ${
                      isActive(tab.href)
                        ? 'text-[#7B1113] bg-[#7B1113]/5 border-l-2 border-[#7B1113]'
                        : 'text-[#8A8A8A] hover:text-[#2A2A2A] hover:bg-[#F0EFED]'
                    }`}
                  >
                    <i className={tab.icon}></i>
                    {tab.label}
                  </Link>
                ))}
                <button
                  onClick={logout}
                  className="w-full flex items-center gap-2 px-5 py-3.5 text-sm text-[#8A8A8A] hover:text-[#7B1113] hover:bg-[#7B1113]/5 transition-colors cursor-pointer"
                >
                  <i className="ri-logout-box-r-line"></i>
                  {t('my.logout', 'Sign Out')}
                </button>
              </div>
            </div>

            {/* Main */}
            <div className="lg:col-span-3">{children}</div>
          </div>
        </div>
      </div>
      <div className="print:hidden">
        <SharedFooter />
      </div>
    </ProtectedRoute>
  );
}